import { Autocomplete, Icon, Text } from "@shopify/polaris"
import { cloneDeep } from "lodash-es"
import { observer } from "mobx-react"
import {
  ComboOption,
  formatHSCode,
  SearchIcon,
  useValidate,
} from "polaris-enhance"
import { useEffect, useState } from "react"

import APIManagement from "@/api/management"

let timer: any = null
const hsCodeMap = {}
async function options(v: string) {
  if (!v) return []
  const key = /^\d/.test(v) ? v.slice(0, 7) : v
  if (!hsCodeMap[key]) {
    const res = await APIManagement.getHsCodeList({ search: key })
    if (res.code === 1000) {
      hsCodeMap[key] = res.data.list || []
    }
  }
  return cloneDeep(hsCodeMap[key])
}

function isValidHSFormat(value: string): boolean {
  // 匹配格式：4位数字.2位数字
  const hsFormatRegex = /^\d{4}\.\d{2}$/
  return hsFormatRegex.test(value)
}

interface HSTextFieldProps {
  value: string
  onChange: (value: string, isFormatted?: boolean) => void
  id?: string
  required?: boolean
}
const HSTextField = (props: HSTextFieldProps) => {
  const { value, onChange, required = true } = props

  const { id, error, handleFocus, handleBlur, isFocus } = useValidate({
    id: props.id,
    value,
    onlyRequired: true,
    onlyRequiredText: t("RequiredForInternationalShipping"),
    rules: [
      {
        validator(rule, value) {
          if (required && !value) {
            return t("Required")
          }
          if (value && !/^\d{4}\.\d{2}[\.\d]*$/.test(value)) {
            return "Please enter a valid HS code"
          }
        },
      },
    ],
  })
  const [inputValue, setInputValue] = useState("")

  const [loading, setLoading] = useState(false)

  const [sectionOptions, setSectionOptions] = useState<ComboOption[]>([])

  function handleSelect(selected: string[]) {
    const val = selected[0] || ""
    onChange(val)
    setInputValue(val)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function debounceQueryOptions(v: string, ms = 300) {
    setLoading(true)

    timer && clearTimeout(timer)
    // 设置新的防抖定时器
    timer = setTimeout(() => {
      options(v)
        .then((options) => {
          setSectionOptions(options)
          if (options.length === 1 && isValidHSFormat(v)) {
            handleSelect([options[0].value])
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }, ms) // 300ms 防抖延迟
  }

  function handleChange(val: string) {
    // 判断第一个字符是否是数字
    if (val && /^\d/.test(val)) {
      val = formatHSCode(val)
    }
    setInputValue(val)
    onChange(val)
    debounceQueryOptions(val)
  }

  useEffect(() => {
    if (value !== inputValue && !isFocus) {
      setInputValue(value)
      debounceQueryOptions(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  useEffect(() => {
    if (isFocus) {
      // 主动触发resizeObserver计算高度
      const nodes = document.querySelectorAll(
        ".Polaris-Modal-Dialog__Modal .Polaris-Scrollable",
      )
      if (nodes.length) {
        nodes.forEach((node) => {
          node.scrollTo(0, node.scrollTop + 1)
          node.scrollTo(0, node.scrollTop - 1)
        })
      } else {
        window.scrollTo(0, window.scrollY + 1)
        window.scrollTo(0, window.scrollY - 1)
      }
    }
  }, [sectionOptions, isFocus])

  return (
    <Autocomplete
      options={sectionOptions.map((item) => {
        return {
          label: (
            <div>
              <span className="font-semibold">{item.value}</span> -
              <span>{item.label}</span>
            </div>
          ),
          value: item.value,
        }
      })}
      selected={value ? [value] : []}
      onSelect={(v) => {
        handleSelect(v)
      }}
      loading={loading}
      id={id}
      preferredPosition="above"
      textField={
        <Autocomplete.TextField
          id={id}
          label={t("HarmonizedSystemHsCode")}
          placeholder={t("SearchByProductKeywordOrCode")}
          prefix={<Icon source={SearchIcon} />}
          onChange={handleChange}
          value={inputValue}
          autoComplete={"off"}
          error={error}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      }
      emptyState={
        inputValue ? (
          <div className="p-2">
            <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
              {t("NoResultsFoundFor", {
                replace: { x: inputValue },
              })}
            </Text>
          </div>
        ) : null
      }
    />
  )
}
export default observer(HSTextField)
